import React, { useState, useEffect, useRef } from 'react';

const Arrow = () => (
  <span style={{ display: 'inline-block', marginLeft: 4, transform: 'translateY(-1px)' }}>→</span>
);
const fmt = (n) => '$' + Math.round(n).toLocaleString('en-US');

// Tracks whether the viewport is at or below the mobile breakpoint.
// Synchronous initial read avoids the desktop-on-mobile flash of unstyled.
function useIsMobile(maxWidth = 760) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(`(max-width: ${maxWidth}px)`).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [maxWidth]);
  return isMobile;
}

/* ============== Nav ============== */
// When `minimal` is true the nav renders the brand only (used on legal
// subpages so visitors always have a sticky way back to the home page).
export function Nav({ minimal = false }) {
  return (
    <nav className="mk-nav">
      <div className="mk-nav-inner">
        <a className="mk-brand" href="#top">Sonor</a>
        {!minimal && (
          <>
            <div className="mk-nav-links">
              <a href="#vsl">See it work</a>
              <a href="#engine">The engine</a>
              <a href="#calculator">ROI</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="mk-nav-right">
              <a href="https://cal.com/sonorai/intro-call" target="_blank" rel="noopener noreferrer" className="mk-nav-cta">Book a strategy call <Arrow/></a>
            </div>
          </>
        )}
      </div>
    </nav>
  );
}

/* ============== Hero ============== */
export function Hero() {
  return (
    <section className="mk-hero">
      <div className="mk-shell mk-hero-inner">
        <h1 className="mk-display">
          The Booking Engine.
        </h1>
        <p className="mk-hero-sub">
          45 new booked jobs in 90 days. <em>Or you don’t pay.</em>
        </p>
        <p className="mk-hero-meta"><em>New booked jobs added straight to your calendar, from leads that would have gone to the next contractor on Google Maps.</em></p>
        <div className="mk-cta-row">
          <a className="mk-btn-dark" href="https://cal.com/sonorai/intro-call" target="_blank" rel="noopener noreferrer">Book a strategy call <Arrow/></a>
        </div>
      </div>
      <PartnerStrip/>
    </section>
  );
}

/* ============== Partner Strip ============== */
export function PartnerStrip() {
  const base = process.env.PUBLIC_URL || '';

  // Home-service CRMs first (what HVAC owners recognize on first glance),
  // then AI infrastructure. CRM order: Jobber, Housecall Pro, ServiceTitan,
  // GoHighLevel.
  const slots = [
    { kind: 'img',  alt: 'Jobber',          file: 'jobber.svg' },
    { kind: 'img',  alt: 'Housecall Pro',   file: 'housecall-pro.svg' },
    { kind: 'img',  alt: 'ServiceTitan',    file: 'servicetitan.svg' },
    { kind: 'img',  alt: 'GoHighLevel',     file: 'gohighlevel.svg' },
    { kind: 'pair', name: 'OpenAI',         icon: 'openai-color.svg' },
    { kind: 'pair', name: 'Claude',         icon: 'claude-color.svg' },
    { kind: 'pair', name: 'Gemini',         icon: 'gemini-color.svg' },
    { kind: 'pair', name: 'ElevenLabs',     icon: 'elevenlabs-color.svg' },
    { kind: 'pair', name: 'n8n',            icon: 'n8n-color.svg' },
    { kind: 'img',  alt: 'Twilio',          file: 'twilio.svg' },
  ];
  const all = [...slots, ...slots];
  return (
    <div className="mk-partners">
      <div className="mk-shell">
        <div className="mk-partners-l">WORKS WITH YOUR STACK</div>
      </div>
      <div className="mk-marquee">
        <div className="mk-marquee-fade mk-marquee-fade-l"/>
        <div className="mk-marquee-fade mk-marquee-fade-r"/>
        <div className="mk-marquee-track">
          {all.map((slot, i) => (
            slot.kind === 'pair' ? (
              <span key={i} className="mk-marquee-logo mk-marquee-pair" aria-label={slot.name}>
                <img src={`${base}/logos/${slot.icon}`} alt="" className="mk-marquee-icon" loading="eager" decoding="async" />
                <span className="mk-marquee-name">{slot.name}</span>
              </span>
            ) : (
              <span key={i} className="mk-marquee-logo" aria-label={slot.alt}>
                <img src={`${base}/logos/${slot.file}`} alt={slot.alt} loading="eager" decoding="async" />
              </span>
            )
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============== VSL ============== */
export function VSL() {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [unmuted, setUnmuted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const init = () => {
      if (cancelled || !iframeRef.current || playerRef.current) return;
      if (!window.YT || !window.YT.Player) return;
      playerRef.current = new window.YT.Player(iframeRef.current);
    };

    if (window.YT && window.YT.Player) {
      init();
    } else {
      if (!document.getElementById('youtube-iframe-api')) {
        const tag = document.createElement('script');
        tag.id = 'youtube-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === 'function') prev();
        init();
      };
    }

    return () => {
      cancelled = true;
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try { playerRef.current.destroy(); } catch (e) { /* noop */ }
        playerRef.current = null;
      }
    };
  }, []);

  const handleUnmute = () => {
    if (unmuted) return;
    const p = playerRef.current;
    if (p) {
      try {
        p.unMute();
        p.setVolume(50);
        p.playVideo();
      } catch (e) { /* noop */ }
    }
    setUnmuted(true);
  };

  return (
    <section id="vsl" className="mk-section">
      <div className="mk-shell">
        <div className="mk-section-head" style={{ textAlign: 'center', margin: '0 auto 56px', maxWidth: '860px' }}>
          <div className="mk-eyebrow">SEE IT WORK</div>
          <h2 className="mk-h1">Watch how it actually books a job.</h2>
        </div>
        <div className="mk-video-wrap">
          <iframe
            ref={iframeRef}
            id="vsl-yt-player"
            className="mk-video"
            src="https://www.youtube-nocookie.com/embed/HOrQ_cdGc54?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1&controls=1&enablejsapi=1"
            title="Sonor AI demo"
            frameBorder="0"
            allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
          {!unmuted && (
            <button
              type="button"
              className="mk-video-unmute"
              onClick={handleUnmute}
              aria-label="Click to unmute"
            >
              <span className="mk-video-unmute-pill">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <line x1="22" y1="9" x2="16" y2="15"/>
                  <line x1="16" y1="9" x2="22" y2="15"/>
                </svg>
                <span>Click for sound</span>
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/* ============== Cal embed ============== */
function CalEmbed({ namespace, elementId, calLink }) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    (function (C, A, L) {
      let p = function (a, ar) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal; let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement('script')).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () { p(api, arguments); };
          const ns = ar[1];
          api.q = api.q || [];
          if (typeof ns === 'string') {
            cal.ns[ns] = cal.ns[ns] || api;
            p(cal.ns[ns], ar);
            p(cal, ['initNamespace', ns]);
          } else {
            p(cal, ar);
          }
          return;
        }
        p(cal, ar);
      };
    })(window, 'https://app.cal.com/embed/embed.js', 'init');

    window.Cal('init', namespace, { origin: 'https://app.cal.com' });
    window.Cal.ns[namespace]('inline', {
      elementOrSelector: '#' + elementId,
      config: { layout: 'month_view', useSlotsViewOnSmallScreen: 'true' },
      calLink,
    });
    window.Cal.ns[namespace]('ui', { hideEventTypeDetails: false, layout: 'month_view' });
  }, [namespace, elementId, calLink]);

  return <div id={elementId} className="mk-cal-frame" />;
}

export function BookCall() {
  const isMobile = useIsMobile();
  return (
    <section id="book" className="mk-section mk-section-snow">
      <div className="mk-shell">
        <div className="mk-section-head" style={{ textAlign: 'center', margin: '0 auto 48px', maxWidth: '780px' }}>
          <div className="mk-eyebrow">BOOK A CALL</div>
          <h2 className="mk-h1">Book your strategy call.</h2>
          <p className="mk-sub" style={{ margin: '18px auto 0' }}>See exactly how much revenue you are leaving on the table.</p>
        </div>
        {isMobile ? (
          <div className="mk-cal-mobile">
            <a className="mk-btn-dark mk-btn-large" href="https://cal.com/sonorai/intro-call" target="_blank" rel="noopener noreferrer">Book a strategy call <Arrow/></a>
          </div>
        ) : (
          <div className="mk-cal-card">
            <CalEmbed namespace="intro-call-mid" elementId="cal-inline-mid" calLink="sonorai/intro-call" />
          </div>
        )}
      </div>
    </section>
  );
}

/* ============== Three Jobs (the Booking Engine) ============== */
export function ThreeJobs() {
  const items = [
    {
      n: '01',
      t: 'Speed to lead',
      b: 'Every inbound lead gets a reply in under 60 seconds. Google Ads, SEO, Facebook, your website, Google Business Profile. Wherever the lead came from, it gets answered before they can keep shopping.',
    },
    {
      n: '02',
      t: 'Call capture',
      b: 'Every call answered in your brand voice. Emergency jobs at 11pm, overflow during summer peak, lunch-hour callers, weekend homeowners. All of it booked straight onto your calendar. Jobs that would have gone to the next contractor on Google now go to you.',
    },
    {
      n: '03',
      t: 'Database revival',
      b: 'Past customers are your warmest leads. We reach out on demand for pre-season tune-ups, equipment upgrade campaigns, slow-week fillers, whatever your calendar needs. Old customers who haven’t called in a year start booking again.',
    },
  ];

  const gridRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section id="engine" className="mk-section">
      <div className="mk-shell">
        <div className="mk-section-head">
          <div className="mk-eyebrow">THREE JOBS. ONE SYSTEM.</div>
          <h2 className="mk-h1">The booking engine.</h2>
          <p className="mk-sub" style={{ margin: '18px 0 0' }}>Built custom for your shop. Three jobs running in parallel, day and night.</p>
        </div>
        <div className={`mk-three ${visible ? 'is-visible' : ''}`} ref={gridRef}>
          {items.map((it, i) => (
            <article
              className="mk-three-card"
              key={i}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="mk-three-n">{it.n}</div>
              <h3 className="mk-three-t">{it.t}</h3>
              <p className="mk-three-b">{it.b}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== Calculator ============== */
function Slider({ label, value, min, max, step, format, onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="mk-slider">
      <div className="mk-slider-row">
        <span className="mk-slider-l">{label}</span>
        <span className="mk-slider-v">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mk-slider-i"
        style={{ background: `linear-gradient(to right, var(--fg-0) 0%, var(--fg-0) ${pct}%, var(--line-2) ${pct}%, var(--line-2) 100%)` }}
      />
      <div className="mk-slider-ticks">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

export function Calculator() {
  const [ticket, setTicket] = useState(350);
  const [calls, setCalls] = useState(25);
  const [missRate, setMissRate] = useState(30);
  const annual = ticket * calls * 365 * (missRate / 100);
  const monthly = annual / 12;
  const missedDaily = calls * (missRate / 100);
  const missedMonthly = missedDaily * 30;

  const calcRef = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = calcRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section id="calculator" className="mk-section mk-section-snow">
      <div className="mk-shell">
        <div className="mk-section-head" style={{ textAlign: 'center', margin: '0 auto 56px', maxWidth: '820px' }}>
          <div className="mk-eyebrow">ROI</div>
          <h2 className="mk-h1">What missed calls<br/>are actually costing you.</h2>
          <p className="mk-sub" style={{ margin: '18px auto 0' }}>Move the sliders. Watch the number.</p>
        </div>
        <div className={`mk-calc ${visible ? 'is-visible' : ''}`} ref={calcRef}>
          <div className="mk-calc-inputs">
            <Slider label="Average ticket value"  value={ticket}   min={100} max={1000} step={50} format={(v) => '$' + v} onChange={setTicket} />
            <Slider label="Daily incoming calls"  value={calls}    min={5}   max={100}  step={5}  format={(v) => v}        onChange={setCalls} />
            <Slider label="Miss rate"             value={missRate} min={5}   max={80}   step={5}  format={(v) => v + '%'}  onChange={setMissRate} />
            <p className="mk-calc-foot">Based on {calls} calls a day at ${ticket} avg ticket with a {missRate}% miss rate.<br/>That's about {Math.round(missedDaily)} missed calls a day, {Math.round(missedMonthly)} a month.</p>
          </div>
          <div className="mk-calc-out">
            <div className="mk-calc-l">YOUR ANNUAL LEAK</div>
            <div className="mk-calc-big">{fmt(annual)}</div>
            <div className="mk-calc-sub">left on the table every year</div>
            <div className="mk-calc-divider"/>
            <div className="mk-calc-pill">
              <span className="mk-calc-pill-n">{fmt(monthly)}</span>
              <span className="mk-calc-pill-l">a month, gone</span>
            </div>
          </div>
        </div>
        <div className="mk-calc-cta">
          <a className="mk-btn-dark" href="https://cal.com/sonorai/intro-call" target="_blank" rel="noopener noreferrer">Book a strategy call <Arrow/></a>
        </div>
      </div>
    </section>
  );
}

/* ============== Stats ============== */
export function Stats() {
  const stats = [
    {
      n: '$172K+',
      l: 'average annual revenue lost to missed calls',
      src: 'Invoca / BIA Kelsey, 2023',
    },
    {
      n: '85%',
      l: 'of after-hours callers never hear back',
      src: 'Forbes Council, 2022',
    },
    {
      n: '62%',
      l: 'of callers choose a competitor after a voicemail',
      src: 'Hibu small business study',
    },
  ];

  const gridRef = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section id="results" className="mk-section">
      <div className="mk-shell">
        <div className="mk-section-head" style={{ textAlign: 'center', margin: '0 auto 64px', maxWidth: '820px' }}>
          <div className="mk-eyebrow">THE COST OF MISSING CALLS</div>
          <h2 className="mk-h1">The math is brutal.</h2>
        </div>
        <div className={`mk-stats ${visible ? 'is-visible' : ''}`} ref={gridRef}>
          {stats.map((s, i) => (
            <div className="mk-stat" key={i} style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="mk-stat-n">{s.n}</div>
              <div className="mk-stat-l">{s.l}</div>
              <span className="mk-stat-src">Source: {s.src}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== Customer Video Testimonials ============== */
export function VideoTestimonials() {
  const base = process.env.PUBLIC_URL || '';
  const testimonials = [
    {
      id: 1,
      name: 'Rey Vicentini',
      role: 'Key Biscayne Painters',
      quote: 'Every call gets answered. Sales and customer support all run through the AI now.',
      poster: `${base}/testimonials/rey-poster.jpg`,
      video: `${base}/testimonials/rey.mp4`,
    },
    {
      id: 2,
      name: 'Dan Tieman',
      role: 'Stealth',
      quote: 'Reach faster, qualify faster, drive more revenue. Massive for us and the team.',
      poster: `${base}/testimonials/dan-poster.jpg`,
      video: `${base}/testimonials/dan.mp4`,
    },
    {
      id: 3,
      name: 'Jason Wallace',
      role: 'JTWI',
      quote: 'Setup was smooth, response time fast. If you’re exploring voice AI, this is the team.',
      poster: `${base}/testimonials/jason-poster.jpg`,
      video: `${base}/testimonials/jason.mp4`,
    },
  ];

  const gridRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(null); // null | testimonial object

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Close modal on Escape, lock body scroll while open
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [active]);

  return (
    <section className="mk-section" id="testimonials">
      <div className="mk-shell">
        <div className="mk-section-head" style={{ textAlign: 'center', margin: '0 auto 56px', maxWidth: '820px' }}>
          <div className="mk-eyebrow">IN THEIR WORDS</div>
          <h2 className="mk-h1">Owners who picked up first.</h2>
          <p className="mk-sub" style={{ margin: '18px auto 0' }}>Real builds, in their words.</p>
        </div>

        <div className={`mk-vt-grid ${visible ? 'is-visible' : ''}`} ref={gridRef}>
          {testimonials.map((t, i) => (
            <figure
              key={t.id}
              className="mk-vt-card"
              style={{ transitionDelay: `${i * 200}ms` }}
            >
              <button
                type="button"
                className="mk-vt-thumb"
                aria-label={`Play testimonial from ${t.name}`}
                onClick={() => setActive(t)}
                onMouseEnter={(e) => {
                  const v = e.currentTarget.querySelector('video');
                  if (v) { v.play().catch(() => { /* autoplay denied, no-op */ }); }
                }}
                onMouseLeave={(e) => {
                  const v = e.currentTarget.querySelector('video');
                  if (v) { v.pause(); v.currentTime = 0; }
                }}
              >
                <video
                  className="mk-vt-video"
                  src={t.video}
                  poster={t.poster}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                />
                <span className="mk-vt-play" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 2L12 7L3 12V2Z"/></svg>
                </span>
              </button>
              <figcaption className="mk-vt-cap">
                <span className="mk-vt-num">0{i + 1}</span>
                <div className="mk-vt-name-row">
                  <span className="mk-vt-name">{t.name}</span>
                  <span className="mk-vt-role">{t.role}</span>
                </div>
              </figcaption>
              <blockquote className="mk-vt-q">
                <span className="mk-vt-q-mark" aria-hidden="true">“</span>{t.quote}
              </blockquote>
            </figure>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="mk-vt-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`Testimonial from ${active.name}`}
          onClick={(e) => { if (e.target === e.currentTarget) setActive(null); }}
        >
          <button
            type="button"
            className="mk-vt-close"
            aria-label="Close testimonial"
            onClick={() => setActive(null)}
          >×</button>
          <div className="mk-vt-stage">
            <video
              className="mk-vt-stage-video"
              src={active.video}
              poster={active.poster}
              controls
              autoPlay
              playsInline
              controlsList="nodownload noplaybackrate noremoteplayback"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
            />
            <div className="mk-vt-stage-meta">
              <div className="mk-vt-stage-name">{active.name}</div>
              <div className="mk-vt-stage-role">{active.role}</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ============== Videos / YouTube channel ============== */
export function Videos() {
  const staticVids = [
    { id: 'HOrQ_cdGc54', t: 'I Built an AI Agent That Makes HVAC Companies $160,000+/Year' },
    { id: 'CNNSoKLL414', t: 'This HVAC AI Blueprint Is Making $140K+ (Full Breakdown)' },
    { id: 'R1WXOypfMKI', t: 'I Built an AI Agent That Prints $180,000+/Year for HVAC Companies' },
  ];
  const [dynamicVids, setDynamicVids] = useState([]);

  useEffect(() => {
    fetch('/api/latest-videos')
      .then((r) => r.json())
      .then((data) => {
        if (!data || !Array.isArray(data.videos)) return;
        // Dedupe against the static featured row so we never show the
        // same video twice if it happens to be both featured and recent.
        const staticIds = new Set(staticVids.map((v) => v.id));
        const fresh = data.videos
          .filter((v) => !staticIds.has(v.id))
          .map((v) => ({ id: v.id, t: v.title }));
        setDynamicVids(fresh.slice(0, 3));
      })
      .catch(() => { /* silent: just skip the dynamic row */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allVids = [...staticVids, ...dynamicVids];

  const gridRef = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="mk-section mk-section-snow">
      <div className="mk-shell">
        <div className="mk-section-head" style={{ textAlign: 'center', margin: '0 auto 56px', maxWidth: '820px' }}>
          <div className="mk-eyebrow">FROM THE FOUNDER</div>
          <h2 className="mk-h1">Why HVAC owners are<br/>making the switch.</h2>
          <p className="mk-sub" style={{ margin: '18px auto 0' }}>
            Top breakdowns from{' '}
            <a className="mk-link" href="https://www.youtube.com/@MohamedElgazzarAI/" target="_blank" rel="noopener noreferrer">@MohamedElgazzarAI</a>.
          </p>
        </div>
        <div className={`mk-vids ${visible ? 'is-visible' : ''}`} ref={gridRef}>
          {allVids.map((v, i) => (
            <a className="mk-vid" key={v.id} href={`https://youtu.be/${v.id}`} target="_blank" rel="noopener noreferrer" style={{ transitionDelay: `${i * 100}ms` }}>
              <div className="mk-vid-thumb">
                <img src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`} alt="" loading="lazy" />
                <div className="mk-vid-play">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 2L12 7L3 12V2Z"/></svg>
                </div>
              </div>
              <div className="mk-vid-meta">
                <h3 className="mk-vid-t">{v.t}</h3>
              </div>
            </a>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <a className="mk-btn-ghost" href="https://www.youtube.com/@MohamedElgazzarAI/" target="_blank" rel="noopener noreferrer">
            Watch more on YouTube <Arrow/>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============== FAQ ============== */
export function FAQ() {
  const items = [
    { q: 'What exactly does Sonor build for me?',          a: 'A fully custom voice agent for your business. It catches every call your team can’t, qualifies the lead, and books the appointment straight into your calendar. Not a subscription tool. It lives on your accounts and is built around how your business actually runs.' },
    { q: 'How is this different from an answering service?', a: 'Answering services take a message and hope someone calls back. Sonor holds a full conversation, qualifies the caller, and books the appointment straight into your calendar. Your customer hangs up with a slot, not a promise.' },
    { q: 'How does the process work?',                       a: 'Strategy call to understand how your business runs. Scope the build together with fixed pricing and phased delivery. Most builds go from kickoff to live in 3 to 5 weeks. Every build is custom because every business is.' },
    { q: 'What does it cost?',                               a: 'Builds typically run $4,000 to $15,000 one-time depending on scope. No subscription, no monthly. Optional support retainer if you want ongoing tuning, but the system runs without it. We give you a fixed quote on the strategy call.' },
    { q: 'What software does it integrate with?',           a: 'ServiceTitan, Housecall Pro, Jobber, GoHighLevel, FieldEdge natively, and virtually anything else with an API or webhook. We do a full feasibility review of your stack on the strategy call.' },
    { q: 'Can I customize how it sounds?',                   a: 'Completely. Voice, tone, greeting line, conversation flow, how it handles different call types. By the time we go live, callers think they’re talking to your front desk.' },
    { q: 'How long does it take to go live?',                a: 'Most builds go from kickoff to live in 3 to 5 weeks. Bigger scopes take longer. We set the timeline on the strategy call before any work begins.' },
    { q: 'What if it makes a mistake on a call?',            a: 'Every agent is built with safe-fall behavior. If it can’t handle something, it captures the details and routes the call to your team for next-day follow-up, no call ever just dies. Plus 30 days of post-launch tuning are included on every build, so we patch every edge case the moment it shows up.' },
    { q: 'Do I own the system after it is built?',           a: 'Yes. Everything runs on your accounts. Once we hand it over, you are not locked into anything. No ongoing fees, no dependency. Support is available if you want it, never required.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="mk-section">
      <div className="mk-shell mk-shell-narrow">
        <div className="mk-section-head" style={{ textAlign: 'left' }}>
          <div className="mk-eyebrow">FAQ</div>
          <h2 className="mk-h1">Frequently asked.</h2>
        </div>
        <div className="mk-faqs">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div className={`mk-faq ${isOpen ? 'is-open' : ''}`} key={i} onClick={() => setOpen(isOpen ? -1 : i)}>
                <div className="mk-faq-row">
                  <div className="mk-faq-q">{it.q}</div>
                  <div className="mk-faq-ic">{isOpen ? '−' : '+'}</div>
                </div>
                {isOpen && <div className="mk-faq-a">{it.a}</div>}
              </div>
            );
          })}
        </div>
        <div className="mk-faq-foot">
          Cannot find what you’re looking for? <a href="https://cal.com/sonorai/intro-call" target="_blank" rel="noopener noreferrer">Book a strategy call.</a>
        </div>
      </div>
    </section>
  );
}

/* ============== Final CTA ============== */
export function FinalCTA() {
  const isMobile = useIsMobile();
  return (
    <section className="mk-section mk-section-snow">
      <div className="mk-shell">
        <div className="mk-section-head" style={{ textAlign: 'center', margin: '0 auto 48px', maxWidth: '860px' }}>
          <div className="mk-eyebrow">READY?</div>
          <h2 className="mk-h1" style={{ fontSize: '68px', letterSpacing: '-2px' }}>Ready to capture<br/><em>every call?</em></h2>
          <p className="mk-sub" style={{ margin: '18px auto 0' }}>We’ll show you exactly what missed calls are costing you.</p>
        </div>
        {isMobile ? (
          <div className="mk-cal-mobile">
            <a className="mk-btn-dark mk-btn-large" href="https://cal.com/sonorai/intro-call" target="_blank" rel="noopener noreferrer">Book a strategy call <Arrow/></a>
          </div>
        ) : (
          <div className="mk-cal-card">
            <CalEmbed namespace="intro-call-final" elementId="cal-inline-final" calLink="sonorai/intro-call" />
          </div>
        )}
      </div>
    </section>
  );
}

/* ============== Footer ============== */
// `minimal` drops the "Pick up first." CTA block so legal subpages don't
// repeat the marketing call-to-action.
export function Footer({ minimal = false }) {
  const year = new Date().getFullYear();
  return (
    <footer className="mk-footer">
      <div className="mk-shell">
        {!minimal && (
          <>
            <div className="mk-foot-cta">
              <h2 className="mk-foot-h">Pick up first.</h2>
              <a className="mk-btn-dark mk-btn-large" href="https://cal.com/sonorai/intro-call" target="_blank" rel="noopener noreferrer">Book a strategy call <Arrow/></a>
            </div>
            <div className="mk-foot-rule"/>
          </>
        )}
        <div className="mk-foot-meta">
          <a className="mk-brand mk-brand-foot" href="#top">Sonor</a>
          <div className="mk-foot-nav">
            <a href="#vsl">See it work</a>
            <a href="#engine">The engine</a>
            <a href="#calculator">ROI</a>
            <a href="#faq">FAQ</a>
            <a href="https://www.youtube.com/@MohamedElgazzarAI/" target="_blank" rel="noopener noreferrer">YouTube</a>
            <a href="https://cal.com/sonorai/intro-call" target="_blank" rel="noopener noreferrer">Book a call</a>
          </div>
        </div>
        <div className="mk-foot-bottom">
          <div className="mk-foot-copy">
            © {year} Sonorai. Sonorai is a brand operated by Elgazzar Ventures LLC.
          </div>
          <div className="mk-foot-legal-nav">
            <a href="#privacy">Privacy Policy</a>
            <a href="#terms">Terms &amp; Conditions</a>
            <a href="#cookies">Cookie Policy</a>
            <a href="#legal-notice">Legal Notice / Impressum</a>
            <a href="#sms-terms">SMS Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
